import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, ConnectionWithProfile } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import { Users, Check, X, UserPlus } from 'lucide-react';

export default function ConnectionsList() {
  const navigate = useNavigate();
  const { currentUser } = useEmberStore();
  const [connections, setConnections] = useState<ConnectionWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (currentUser) {
      loadConnections();
    }
  }, [currentUser]);

  async function loadConnections() {
    if (!currentUser) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('get_user_connections', {
          connection_status: null, // Get all statuses
          limit_count: showAll ? 100 : 10
        });

      if (error) throw error;
      if (data) setConnections(data as ConnectionWithProfile[]);
    } catch (err) {
      console.error('Error loading connections:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAcceptConnection(connectionId: string) {
    setProcessingIds(new Set([...processingIds, connectionId]));
    try {
      const { error } = await supabase.rpc('accept_connection_request', {
        connection_id: connectionId
      });

      if (error) throw error;
      
      toast.success('Connection accepted!');
      loadConnections();
    } catch (err) {
      console.error('Error accepting connection:', err);
      toast.error('Failed to accept connection');
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(connectionId);
        return newSet;
      });
    }
  }

  async function handleRemoveConnection(connectionId: string) {
    const ok = await new Promise<boolean>((resolve) => {
      const modal = document.createElement('div');
      modal.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;`;
      const content = document.createElement('div');
      content.style.cssText = `background:#1a1a1a;border:1px solid var(--color-border);border-radius:12px;padding:2rem;max-width:400px;width:100%;text-align:center;color:#ffffff;`;
      content.innerHTML = `
        <h3 style=\"font-size:1.25rem;margin-bottom:1rem;color:var(--color-accent);\">Remove Connection</h3>
        <p style=\"color:#cfcfcf;margin-bottom:1.5rem;\">Are you sure you want to remove this connection?</p>
        <div style=\"display:flex;gap:1rem;justify-content:center;\">
          <button id=\"cancel\" style=\"padding:0.75rem 1.5rem;background:transparent;color:#999;border:1px solid #666;border-radius:6px;cursor:pointer;font-weight:600;\">Cancel</button>
          <button id=\"confirm\" style=\"padding:0.75rem 1.5rem;background:#e4574d;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:600;\">Remove</button>
        </div>
      `;
      modal.appendChild(content);
      document.body.appendChild(modal);
      content.querySelector('#cancel')?.addEventListener('click', () => { modal.remove(); resolve(false); });
      content.querySelector('#confirm')?.addEventListener('click', () => { modal.remove(); resolve(true); });
    });

    if (!ok) return;
    
    setProcessingIds(new Set([...processingIds, connectionId]));
    try {
      const { error } = await supabase.rpc('remove_connection', {
        connection_id: connectionId
      });

      if (error) throw error;
      
      toast.success('Connection removed');
      loadConnections();
    } catch (err) {
      console.error('Error removing connection:', err);
      toast.error('Failed to remove connection');
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(connectionId);
        return newSet;
      });
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner" style={{ margin: '0 auto' }}></div>
      </div>
    );
  }

  if (connections.length === 0) {
    return null; // Don't show anything if no connections
  }

  const pendingConnections = connections.filter(c => c.status === 'pending' && !c.is_requester);
  const acceptedConnections = connections.filter(c => c.status === 'accepted');
  const sentRequests = connections.filter(c => c.status === 'pending' && c.is_requester);

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={20} color="var(--color-accent)" />
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
            Connections
            {pendingConnections.length > 0 && (
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.2rem 0.5rem',
                backgroundColor: '#e4574d',
                color: 'white',
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: '700'
              }}>
                {pendingConnections.length}
              </span>
            )}
          </h3>
        </div>
      </div>

      {/* Pending Connection Requests (Incoming) */}
      {pendingConnections.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <h4 style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
            Pending Requests
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pendingConnections.map((connection) => (
              <div
                key={connection.connection_id}
                style={{
                  padding: '1rem',
                  backgroundColor: 'rgba(196, 255, 0, 0.1)',
                  border: '1px solid var(--color-accent)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#5a3310',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {connection.avatar_url ? (
                    <img src={connection.avatar_url} alt={connection.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <UserPlus size={24} color="#ff9500" />
                  )}
                </div>

                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    {connection.username}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'capitalize' }}>
                    {connection.smoker_type === 'both' ? 'Pipe & Cigar' : connection.smoker_type} Enthusiast
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleAcceptConnection(connection.connection_id)}
                    disabled={processingIds.has(connection.connection_id)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--color-accent)',
                      border: 'none',
                      color: '#0b0b0b',
                      borderRadius: '6px',
                      cursor: processingIds.has(connection.connection_id) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    <Check size={14} />
                    Accept
                  </button>
                  <button
                    onClick={() => handleRemoveConnection(connection.connection_id)}
                    disabled={processingIds.has(connection.connection_id)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--color-danger)',
                      color: 'var(--color-danger)',
                      borderRadius: '6px',
                      cursor: processingIds.has(connection.connection_id) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    <X size={14} />
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accepted Connections */}
      {acceptedConnections.length > 0 && (
        <div style={{ marginBottom: '0.5rem' }}>
          <h4 style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
            Connected ({acceptedConnections.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {acceptedConnections.slice(0, showAll ? 100 : 5).map((connection) => (
              <div
                key={connection.connection_id}
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#5a3310',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {connection.avatar_url ? (
                    <img src={connection.avatar_url} alt={connection.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <UserPlus size={20} color="#ff9500" />
                  )}
                </div>

                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {connection.username}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'capitalize' }}>
                    {connection.smoker_type === 'both' ? 'Pipe & Cigar' : connection.smoker_type}
                  </div>
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveConnection(connection.connection_id);
                  }}
                  disabled={processingIds.has(connection.connection_id)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    color: '#999',
                    borderRadius: '6px',
                    cursor: processingIds.has(connection.connection_id) ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Requests (Outgoing Pending) */}
      {sentRequests.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
            Sent Requests ({sentRequests.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {sentRequests.slice(0, showAll ? 100 : 3).map((connection) => (
              <div
                key={connection.connection_id}
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  opacity: 0.7
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#5a3310',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {connection.avatar_url ? (
                    <img src={connection.avatar_url} alt={connection.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <UserPlus size={20} color="#ff9500" />
                  )}
                </div>

                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {connection.username}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>
                    Pending...
                  </div>
                </div>

                {/* Cancel button */}
                <button
                  onClick={() => handleRemoveConnection(connection.connection_id)}
                  disabled={processingIds.has(connection.connection_id)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    color: '#999',
                    borderRadius: '6px',
                    cursor: processingIds.has(connection.connection_id) ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show More/Less */}
      {connections.length > 5 && (
        <button
          onClick={() => {
            setShowAll(!showAll);
            if (!showAll) {
              loadConnections();
            }
          }}
          style={{
            width: '100%',
            marginTop: '0.75rem',
            padding: '0.5rem',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-border)',
            color: 'var(--color-accent)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          {showAll ? 'Show less' : `Show all ${connections.length} connections`}
        </button>
      )}
    </div>
  );
}

